import { saveAs } from "file-saver";
import _ from "lodash";
import { call, put, select, takeLatest } from "redux-saga/effects";
import * as XLSX from "xlsx";
import { setCreditMemos } from "../actions";
import {
  base64ToBlob,
  creditMemoColumnWidths,
  creditMemoWorkbookRows,
} from "../components/dashboard/creditMemos/creditMemos.helpers";
import type { CreditMemoSpreadsheet } from "../components/dashboard/creditMemos/creditMemos.helpers";
import { Auth } from "../utilities/auth";
import axios from "../utilities/axiosWrapper";

// Auth reducer
export const selectAuthReducer = (state: any) => state.authReducer;

/**
 * @desc The axios wrapper returns the error object instead of throwing, so on a
 * non 2xx the status and body sit under `response`.
 * @param {*} jsonResponse
 * @return {*} {{ status: number, body: any }}
 */
const readResponse = (jsonResponse: any): { status: number; body: any } => ({
  status: jsonResponse?.status ?? jsonResponse?.response?.status ?? 0,
  body: jsonResponse?.data ?? jsonResponse?.response?.data ?? {},
});

/**
 * @param {*} body
 * @return {string} the API's message, or a generic fallback
 */
const readErrorMessage = (body: any): string =>
  body?.error?.message ?? "Something went wrong. Please try again.";

const ACTION_DONE_MESSAGES: Record<string, string> = {
  approve:
    "Credit memo approved: the PDF was created and the plan admin has been emailed. The money stays in Return and in the tax bucket until it is paid out.",
  taxTransferDone: "The tax transfer at DC Bank has been recorded.",
  confirmBank: "The bank account has been recorded. The memo is ready to pay out.",
  moneySent: "The payout has been recorded and the credit memo is completed.",
  cancel: "The credit memo has been cancelled.",
  void: "The credit memo has been voided: its money stays in Return and in the tax bucket on Aya and its transactions will be offered again on a new memo.",
  sendPayout: "The payout was sent at DC Bank and the credit memo is completed.",
  retryPayout: "The payout was found or sent at DC Bank and the credit memo is completed.",
  resolvePayout: "The payout has been resolved.",
};

// A payout writes on Aya and calls DC Bank before it answers, so even a
// refusal or a lost answer can leave the memo changed: read it again.
const PAYOUT_ACTIONS = ["sendPayout", "retryPayout", "resolvePayout"];

function* refreshAfterPayout(data: any): Generator {
  if (PAYOUT_ACTIONS.includes(data.action)) {
    yield put({
      type: "FETCH_CREDIT_MEMO",
      data: { businessGuid: data.businessGuid, creditMemoGuid: data.creditMemoGuid },
    });
  }
}

/**
 * @desc List credit memos across every business.
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* fetchCreditMemosWorker(action: any): Generator {
  let selectedAuthReducer: any = yield select(selectAuthReducer);
  let params: any = {
    method: "get",
    path: `businesses/credit-memos?status=${action.data}&at=${selectedAuthReducer.accessToken}`,
    data: {},
  };

  try {
    const apikeys: any = yield Auth.getAuthTokens();
    const jsonResponse: any = yield call(axios, params, apikeys);
    const { status, body } = readResponse(jsonResponse);

    if (status >= 200 && status < 300) {
      return yield put(
        setCreditMemos({ memos: _.get(body, "data", []) || [], memosLoading: false })
      );
    }
    return yield put(
      setCreditMemos({ memosLoading: false, memosError: readErrorMessage(body) })
    );
  } catch (ex) {
    return yield put(
      setCreditMemos({
        memosLoading: false,
        memosError: "Could not read the credit memos.",
      })
    );
  }
}

/**
 * @desc Read one credit memo, optionally with the balance check.
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* fetchCreditMemoWorker(action: any): Generator {
  const { businessGuid, creditMemoGuid, balanceCheck } = action.data;
  let selectedAuthReducer: any = yield select(selectAuthReducer);
  let params: any = {
    method: "get",
    path: `businesses/${businessGuid}/credit-memos/${creditMemoGuid}?balanceCheck=${
      balanceCheck ? "true" : "false"
    }&at=${selectedAuthReducer.accessToken}`,
    data: {},
  };

  try {
    const apikeys: any = yield Auth.getAuthTokens();
    const jsonResponse: any = yield call(axios, params, apikeys);
    const { status, body } = readResponse(jsonResponse);

    if (status >= 200 && status < 300) {
      return yield put(
        setCreditMemos({ selected: _.get(body, "data", null), selectedLoading: false })
      );
    }
    return yield put(
      setCreditMemos({
        selectedLoading: false,
        selectedError: readErrorMessage(body),
      })
    );
  } catch (ex) {
    return yield put(
      setCreditMemos({
        selectedLoading: false,
        selectedError: "Could not read the credit memo.",
      })
    );
  }
}

/**
 * @desc Run one step on a credit memo. A 409 is a refusal - most often a
 * balance that does not match - and carries the details for the pop-up.
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* runCreditMemoActionWorker(action: any): Generator {
  const { businessGuid, creditMemoGuid, filter } = action.data;
  let selectedAuthReducer: any = yield select(selectAuthReducer);
  let params: any = {
    method: "post",
    path: `businesses/${businessGuid}/credit-memos/${creditMemoGuid}?at=${selectedAuthReducer.accessToken}`,
    data: { action: action.data.action, ...(action.data.payload || {}) },
  };

  try {
    const apikeys: any = yield Auth.getAuthTokens();
    const jsonResponse: any = yield call(axios, params, apikeys);
    const { status, body } = readResponse(jsonResponse);

    if (status >= 200 && status < 300) {
      yield put(
        setCreditMemos({
          selected: _.get(body, "data", null),
          actionRunning: false,
          actionDone: ACTION_DONE_MESSAGES[action.data.action] || "Done.",
        })
      );
      // The memo may have left the list it was on, so read the list again.
      return yield put({ type: "FETCH_CREDIT_MEMOS", data: filter });
    }

    if (status === 409) {
      yield put(
        setCreditMemos({
          actionRunning: false,
          refusal: _.get(body, "data", null) || {
            reason: _.get(body, "error.code", "refused"),
            message: readErrorMessage(body),
          },
        })
      );
      return yield* refreshAfterPayout(action.data);
    }

    yield put(
      setCreditMemos({
        actionRunning: false,
        refusal: { reason: "error", message: readErrorMessage(body) },
      })
    );
    return yield* refreshAfterPayout(action.data);
  } catch (ex) {
    const payout = PAYOUT_ACTIONS.includes(action.data.action);
    yield put(
      setCreditMemos({
        actionRunning: false,
        refusal: {
          reason: "error",
          message: payout
            ? "No answer came back. The payout may still be running at DC Bank: do not send it again. Open the memo in a few minutes to see where it stands, and check it from there."
            : "The request failed. Nothing was changed; please try again.",
        },
      })
    );
    return yield* refreshAfterPayout(action.data);
  }
}

/**
 * @return {*}  {Generator}
 */
/**
 * @desc Save a credit memo as its PDF, or as an Excel file laid out like it.
 * Both come from the invoice service, so they match the memo sent to the
 * client.
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* exportCreditMemoWorker(action: any): Generator {
  const { businessGuid, creditMemoGuid, format } = action.data;
  let selectedAuthReducer: any = yield select(selectAuthReducer);
  let params: any = {
    method: "get",
    path: `businesses/${businessGuid}/credit-memos/${creditMemoGuid}?export=${format}&at=${selectedAuthReducer.accessToken}`,
    data: {},
  };

  try {
    const apikeys: any = yield Auth.getAuthTokens();
    const jsonResponse: any = yield call(axios, params, apikeys);
    const { status, body } = readResponse(jsonResponse);
    if (status < 200 || status >= 300) {
      return yield put(setCreditMemos({ exportRunning: "", exportError: readErrorMessage(body) }));
    }

    const data: any = _.get(body, "data", null);
    if (format === "pdf") {
      saveAs(base64ToBlob(data.data, data.contentType || "application/pdf"), data.fileName);
    } else {
      const sheet: CreditMemoSpreadsheet = data;
      const rows = creditMemoWorkbookRows(sheet);
      const worksheet = XLSX.utils.aoa_to_sheet(rows);
      worksheet["!cols"] = creditMemoColumnWidths(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, sheet.sheetName || "Credit Memo");
      XLSX.writeFile(workbook, sheet.fileName);
    }
    return yield put(setCreditMemos({ exportRunning: "" }));
  } catch (ex) {
    return yield put(
      setCreditMemos({
        exportRunning: "",
        exportError: "Could not export the credit memo. Please try again.",
      })
    );
  }
}

function* fetchCreditMemosWatcher(): Generator {
  yield takeLatest("FETCH_CREDIT_MEMOS", fetchCreditMemosWorker);
}

/**
 * @return {*}  {Generator}
 */
function* fetchCreditMemoWatcher(): Generator {
  yield takeLatest("FETCH_CREDIT_MEMO", fetchCreditMemoWorker);
}

/**
 * @return {*}  {Generator}
 */
function* runCreditMemoActionWatcher(): Generator {
  yield takeLatest("RUN_CREDIT_MEMO_ACTION", runCreditMemoActionWorker);
}

function* exportCreditMemoWatcher(): Generator {
  yield takeLatest("EXPORT_CREDIT_MEMO", exportCreditMemoWorker);
}

export {
  fetchCreditMemosWatcher,
  fetchCreditMemoWatcher,
  runCreditMemoActionWatcher,
  exportCreditMemoWatcher,
};
